/**
 * AffiliateSignIn — signs an approved affiliate into the portal.
 *
 * The affiliate identity is separate from the member session, so this does not
 * use `AuthContext`: it calls `useAffiliateSession().signIn`, which stores its
 * own bearer. A correct password on an application still awaiting review is a
 * distinct outcome from bad credentials and gets its own message.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAffiliateSession } from '@wlb/shared/context/AffiliateSessionContext';
import { isAffiliateNotApprovedError } from '@wlb/shared/api/affiliate/affiliateErrors';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { getUserFacingErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import { validateEmail } from '@wlb/shared/utils/validation';
import { FormError } from '../components/ui/FormError';
import { PasswordInput } from '../components/ui/PasswordInput';
import { AffiliatePortalLayout } from '../features/affiliate/AffiliatePortalLayout';
import { AFFILIATE_ROUTES } from '../features/affiliate/affiliateRoutes';
import '../components/layout/authFlow.css';
// `auth-flow__link-button` (the forgot-password link) is defined here.
import '../features/auth/auth.css';
import '../features/affiliate/affiliatePortal.css';

export function AffiliateSignIn() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signIn, isSignedIn, expired } = useAffiliateSession();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Navigate on the session flipping, never from inside the submit handler —
  // the same contract the member login screen follows.
  useEffect(() => {
    if (isSignedIn) navigate(AFFILIATE_ROUTES.DASHBOARD, { replace: true });
  }, [isSignedIn, navigate]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError('');

    const emailResult = validateEmail(email);
    const nextEmailError = emailResult.valid ? '' : t(emailResult.message);
    const nextPasswordError = password ? '' : t('validation.passwordRequired');
    setEmailError(nextEmailError);
    setPasswordError(nextPasswordError);
    if (nextEmailError || nextPasswordError) return;

    setSubmitting(true);
    try {
      await signIn(email.trim(), password);
    } catch (error) {
      if (isAffiliateNotApprovedError(error)) {
        setFormError(
          t(
            error.rejected
              ? 'affiliate.signIn.applicationRejected'
              : 'affiliate.signIn.pendingReview',
          ),
        );
      } else {
        // A server fault or a dropped connection is not a wrong password, so
        // the reason is carried through instead of blaming the credentials.
        setFormError(
          getUserFacingErrorMessage(error, t('affiliate.signIn.failed')),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AffiliatePortalLayout centered>
      <div className="auth-flow__card">
        <h1 className="auth-flow__heading">{t('affiliate.signIn.heading')}</h1>
        <p className="auth-flow__subheading">{t('affiliate.signIn.subheading')}</p>
        <form className="auth-flow__form" onSubmit={handleSubmit} noValidate>
          <FormError
            variant="banner"
            message={formError || (expired ? t('affiliate.signIn.sessionExpired') : '')}
            id="affiliate-signin-error"
          />
          <label className="auth-flow__field">
            <span>{t('affiliate.signIn.emailLabel')}</span>
            <input
              className={`auth-flow__input${emailError ? ' auth-flow__input--error' : ''}`}
              type="email"
              value={email}
              onChange={event => {
                setEmail(event.target.value);
                if (emailError) setEmailError('');
              }}
              placeholder={t('affiliate.signIn.emailPlaceholder')}
              autoComplete="email"
              aria-invalid={Boolean(emailError)}
              aria-describedby={emailError ? 'affiliate-signin-email-error' : undefined}
              autoFocus
            />
            <FormError message={emailError} id="affiliate-signin-email-error" />
          </label>
          <label className="auth-flow__field">
            <span>{t('affiliate.signIn.passwordLabel')}</span>
            <PasswordInput
              className={`auth-flow__input${passwordError ? ' auth-flow__input--error' : ''}`}
              value={password}
              onChange={event => {
                setPassword(event.target.value);
                if (passwordError) setPasswordError('');
              }}
              placeholder={t('affiliate.signIn.passwordPlaceholder')}
              autoComplete="current-password"
              aria-invalid={Boolean(passwordError)}
              aria-describedby={
                passwordError ? 'affiliate-signin-password-error' : undefined
              }
            />
            <FormError message={passwordError} id="affiliate-signin-password-error" />
          </label>
          <Link
            className="auth-flow__link-button affiliate-portal__forgot-password"
            to={AFFILIATE_ROUTES.FORGOT_PASSWORD}
          >
            {t('auth.loginPassword.forgotPassword')}
          </Link>
          <button className="auth-flow__primary" type="submit" disabled={submitting}>
            {submitting ? t('common.loading') : t('common.buttons.signIn')}
          </button>
        </form>
        <p className="auth-flow__footer">
          {t('affiliate.signIn.noAccount')}
          <Link to={AFFILIATE_ROUTES.SIGN_UP}>{t('affiliate.nav.join')}</Link>
        </p>
      </div>
    </AffiliatePortalLayout>
  );
}
